<template>
    <div class="cards-view">
        <div v-if="isSearchEmpty" class="cards-view__empty">
            <div class="cards-view__empty-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="6.5" stroke="currentColor" stroke-width="1.8" />
                    <path d="M16 16l4.5 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                </svg>
            </div>
            <div class="cards-view__empty-title">Проекты не найдены</div>
            <div class="cards-view__empty-text">
                По запросу «{{ searchInput.trim() }}» ничего нет. Попробуйте другое название или ID.
            </div>
            <button type="button" class="cards-view__empty-btn" @click="clearSearch">
                Сбросить поиск
            </button>
        </div>
        <div v-else class="cards-view__grid">
            <ProjectCard
                v-for="project in paginatedProjects"
                :key="project.id"
                :project="project"
            />
            <button type="button" class="project-create-card" @click="goToCreateProject">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                        d="M12 5v14M5 12h14"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                    />
                </svg>
                <span>Создать проект</span>
            </button>
        </div>
        <div v-if="projects.length > 6 && !isSearchEmpty" class="cards-view__footer">
            <div class="cards-view__per-page">
                <span class="select-label">На странице</span>
                <v-select
                    class="per-page-select"
                    :items="itemsOptions"
                    item-text="text"
                    item-value="value"
                    v-model="itemsPerPage.value"
                    hide-details
                    dense
                    :menu-props="{ offsetY: true, contentClass: 'cards-view-per-page-menu' }"
                />
            </div>
            <v-pagination
                v-if="totalPages > 1"
                class="cards-view__pagination"
                v-model="currentPage"
                :length="totalPages"
                :total-visible="5"
            />
        </div>
    </div>
</template>

<script>
import debounce from 'lodash/debounce'
import ProjectCard from './ProjectCard'

const PAGE_SIZE_ALL = -1

export default {
    name: 'CardsView',
    props: {
        projects: {
            type: Array,
            default: [],
        },
        searchInput: {
            type: String,
            default: '',
        },
    },
    components: { ProjectCard },
    data: () => ({
        currentPage: 1,
        itemsPerPage: { text: 15, value: 15 },
        appliedQuery: '',
        matchedProjects: null,
    }),
    computed: {
        itemsOptions() {
            return [
                { text: 6, value: 6 },
                { text: 15, value: 15 },
                { text: 24, value: 24 },
                { text: `Все (${this.projects.length})`, value: PAGE_SIZE_ALL },
            ]
        },
        filteredProjects() {
            return this.matchedProjects == null ? this.projects : this.matchedProjects
        },
        isSearchEmpty() {
            return Boolean(this.appliedQuery && !this.filteredProjects.length)
        },
        pageSize() {
            const value = this.itemsPerPage.value
            if (value === PAGE_SIZE_ALL) {
                return Math.max(this.filteredProjects.length, 1)
            }
            return value
        },
        totalPages() {
            return Math.max(1, Math.ceil(this.filteredProjects.length / this.pageSize))
        },
        paginatedProjects() {
            const list = this.filteredProjects
            if (this.itemsPerPage.value === PAGE_SIZE_ALL) {
                return list
            }
            const start = (this.currentPage - 1) * this.pageSize
            return list.slice(start, start + this.pageSize)
        },
    },
    watch: {
        searchInput(value) {
            if (!value) {
                if (this.commitSearch && this.commitSearch.cancel) {
                    this.commitSearch.cancel()
                }
                this.commitSearchNow()
                return
            }
            this.commitSearch()
        },
        'itemsPerPage.value'() {
            this.currentPage = 1
        },
        filteredProjects() {
            if (this.currentPage > this.totalPages) {
                this.currentPage = this.totalPages
            }
        },
    },
    created() {
        this.commitSearch = debounce(this.commitSearchNow, 200)
        this.$on('deleteProject', this.deleteProject)
        this.$on('addUser', this.addUser)
        this.$on('renameProject', this.renameProject)
        this.$on('changeProjectImage', this.changeProjectImage)
    },
    beforeDestroy() {
        if (this.commitSearch && this.commitSearch.cancel) {
            this.commitSearch.cancel()
        }
    },
    methods: {
        commitSearchNow() {
            const query = this.searchInput.trim().toLowerCase()
            this.appliedQuery = query
            this.currentPage = 1
            if (!query) {
                this.matchedProjects = null
                return
            }
            const list = this.projects
            const matched = []
            for (let i = 0, n = list.length; i < n; i++) {
                const project = list[i]
                const name = project && project.name != null ? String(project.name) : ''
                const id = project && project.id != null ? String(project.id) : ''
                if (name.toLowerCase().indexOf(query) !== -1 || id.toLowerCase().indexOf(query) !== -1) {
                    matched.push(project)
                }
            }
            this.matchedProjects = Object.freeze(matched)
        },
        clearSearch() {
            this.$emit('clear-search')
        },
        goToCreateProject() {
            this.$router.push({ name: 'OnBoardingCreateProject' })
        },
        addUser(projectId) {
            this.$parent.$emit('addUser', projectId)
        },
        deleteProject(projectId) {
            this.$parent.$emit('deleteProject', projectId)
        },
        renameProject(projectId) {
            this.$parent.$emit('renameProject', projectId)
        },
        changeProjectImage(projectId) {
            this.$parent.$emit('changeProjectImage', projectId)
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.cards-view__empty
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    text-align: center
    margin-top: 8px
    padding: 48px 24px
    background: #fff
    border: 1px solid #E6E9F0
    border-radius: 16px
    box-shadow: 0 1px 2px rgba(26, 26, 46, .04)

.cards-view__empty-icon
    width: 56px
    height: 56px
    border-radius: 16px
    background: $bcg-card-new
    color: $main-new !important
    display: flex
    align-items: center
    justify-content: center
    margin-bottom: 16px

    svg
        width: 26px
        height: 26px

.cards-view__empty-title
    font-family: $font-family-base !important
    font-size: 18px !important
    font-weight: 800 !important
    letter-spacing: -.02em
    line-height: 1.25
    color: $dark-new !important
    margin-bottom: 8px

.cards-view__empty-text
    max-width: 420px
    font-family: $font-family-base !important
    font-size: 14px !important
    font-weight: 500 !important
    line-height: 1.5
    color: $muted-new !important
    margin-bottom: 18px
    overflow-wrap: anywhere

.cards-view__empty-btn
    display: inline-flex
    align-items: center
    justify-content: center
    margin: 0
    border: 1.5px solid rgba(82, 90, 213, .25)
    border-radius: 10px
    background: transparent
    padding: 10px 16px
    font-family: $font-family-base !important
    font-size: 12px !important
    font-weight: 800 !important
    letter-spacing: .04em
    text-transform: uppercase
    color: $main-new !important
    cursor: pointer
    transition: background .15s, border-color .15s, transform .15s

    &:hover
        background: $bcg-card-new
        border-color: $main-new
        transform: translateY(-1px)

.cards-view__grid
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(#{'min(100%, 260px)'}, 1fr))
    gap: 18px
    align-items: stretch

.project-create-card
    box-sizing: border-box
    min-height: 160px
    width: 100%
    margin: 0
    padding: 24px
    border: 1.5px dashed #E6E9F0
    border-radius: 14px
    background: transparent
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    gap: 9px
    color: #AAB2C4 !important
    cursor: pointer
    transition: border-color .15s, color .15s, background .15s

    svg
        width: 24px
        height: 24px
        flex-shrink: 0

    span
        font-family: $font-family-base !important
        font-size: 11.5px !important
        font-weight: 800 !important
        letter-spacing: .04em
        text-transform: uppercase
        color: inherit !important
        line-height: 1.2

    &:hover
        border-color: $main-new
        color: $main-new !important
        background: $bcg-card-new

    &:focus-visible
        outline: 2px solid rgba($main-new, .45)
        outline-offset: 2px

.select-label
    font-family: $font-family-base !important
    font-size: 11.5px !important
    font-weight: 800 !important
    letter-spacing: .04em
    text-transform: uppercase
    color: $muted-new !important
    white-space: nowrap

.cards-view__footer
    display: flex
    align-items: center
    justify-content: flex-end
    flex-wrap: wrap
    gap: 14px 18px
    margin-top: 20px
    padding-top: 16px
    border-top: 1px solid #EFF1F6

.cards-view__per-page
    display: flex
    align-items: center
    gap: 10px

.per-page-select
    font-family: $font-family-base
    width: 118px
    flex: 0 0 auto
    margin: 0 !important
    padding: 0

    ::v-deep
        .v-input__control
            min-height: 36px

        .v-input__slot
            margin: 0 !important
            padding: 0 4px 0 12px !important
            min-height: 36px !important
            height: 36px
            background: #fff !important
            border: 1px solid #E6E9F0
            border-radius: 10px
            box-shadow: none !important
            transition: border-color .15s, box-shadow .15s
            align-items: center

            &:before,
            &:after
                display: none !important

        .v-select__slot
            display: flex
            align-items: center
            height: 36px
            min-width: 0

        .v-select__selections
            display: flex
            flex-wrap: nowrap
            align-items: center
            min-width: 0
            height: 36px
            max-height: 36px
            padding: 0 !important
            overflow: hidden

        .v-select__selection
            max-width: 100%
            margin: 0 4px 0 0 !important
            overflow: hidden
            text-overflow: ellipsis
            white-space: nowrap
            font-family: $font-family-base !important
            font-size: 13px !important
            font-weight: 700 !important
            line-height: 36px !important
            color: $dark-new !important

        .v-select__slot input
            position: absolute
            width: 0
            height: 0
            margin: 0
            padding: 0
            opacity: 0

        .v-input__append-inner
            margin: 0 !important
            padding: 0 !important
            align-self: center

        .v-input__icon
            height: 36px
            min-width: 24px

        .v-input__icon .v-icon
            color: $muted-new !important
            font-size: 18px
            caret-color: $muted-new

        .v-input__slot:hover
            border-color: $main-new

    &.v-input--is-focused ::v-deep .v-input__slot
        border-color: $main-new
        box-shadow: 0 0 0 3px rgba(82, 90, 213, .12) !important

.cards-view__pagination
    margin: 0

::v-deep
    .v-pagination
        margin: 0
        justify-content: flex-end

    .v-pagination__item,
    .v-pagination__navigation
        width: 34px !important
        min-width: 34px !important
        height: 34px !important
        margin: 0 3px !important
        padding: 0 !important
        border: 1px solid #E6E9F0 !important
        border-radius: 10px !important
        background: #fff !important
        box-shadow: none !important
        color: $muted-new !important
        font-family: $font-family-base !important
        font-size: 13px !important
        font-weight: 700 !important
        line-height: 34px !important
        transition: border-color .15s, background .15s, color .15s, box-shadow .15s, transform .15s

    .v-pagination__navigation .v-icon
        color: $muted-new !important
        font-size: 18px !important

    .v-pagination__item:hover,
    .v-pagination__navigation:hover
        border-color: $main-new !important
        color: $main-new !important
        background: $bcg-card-new !important
        transform: translateY(-1px)

    .v-pagination__navigation:hover .v-icon
        color: $main-new !important

    .v-pagination__item--active
        border-color: $main-new !important
        background: $main-new !important
        box-shadow: 0 3px 14px rgba(82, 90, 213, .28) !important
        color: #fff !important
        transform: none

        &:hover
            background: #454CC0 !important
            border-color: #454CC0 !important
            color: #fff !important

    .v-pagination__navigation--disabled
        opacity: .38
        pointer-events: none
        transform: none

    .v-pagination__more
        margin: 0 2px
        color: $muted-new !important
        font-weight: 700

@media (max-width: 680px)
    .cards-view__footer
        justify-content: center

    ::v-deep .v-pagination
        justify-content: center
</style>

<style lang="sass">
@import '@/vars.sass'

.cards-view-per-page-menu
    border-radius: 12px !important
    border: 1px solid #E6E9F0
    box-shadow: 0 10px 24px rgba(26, 26, 46, .08) !important
    margin-top: 4px
    overflow: hidden

    .v-list
        padding: 6px !important
        background: #fff !important

    .v-list-item
        min-height: 36px !important
        border-radius: 8px
        font-family: $font-family-base !important
        font-size: 13px !important
        font-weight: 700 !important
        color: $dark-new !important

    .v-list-item:hover
        background: $bcg-card-new !important

    .v-list-item--active
        color: $main-new !important
        background: $bcg-card-new !important
</style>
